import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  createPartnerDealerUser,
  fetchPartnerDealer,
  fetchPartnerDealerUsers,
  startPartnerDealerPortalSession,
  updatePartnerDealer,
  updatePartnerDealerUser,
} from "../../services/automapPartnerDealerApi";
import { rememberPartnerDealerPortalTarget } from "../../constants/automapPartnerDealerPortal";
import { usePartnerPortalBase } from "../../components/partner/PartnerPortalBaseProvider";
import "../../styles/partnerDealers.css";

const STATUS_OPTIONS = ["prospect", "onboarding", "active", "paused", "suspended", "archived"];
const USER_ROLES = ["owner", "manager", "salesperson", "inventory_manager", "viewer"];

const ROLE_LABELS = {
  owner: "Owner",
  manager: "Manager",
  salesperson: "Sales",
  inventory_manager: "Inventory",
  viewer: "View only",
};

function emptyUserForm() {
  return {
    name: "",
    email: "",
    whatsapp: "",
    role: "manager",
    password: "",
  };
}

function formatStatusLabel(value) {
  if (!value) return "—";
  return String(value).replace(/_/g, " ");
}

function UserStatusBadge({ status }) {
  const tone =
    status === "active" ? "is-active" : status === "invited" ? "is-invited" : "is-muted";
  return (
    <span className={`partner-dealer-account__status-badge ${tone}`}>{formatStatusLabel(status)}</span>
  );
}

export default function PartnerDealerDetailPage() {
  const { dealerId } = useParams();
  const { path } = usePartnerPortalBase();
  const [dealer, setDealer] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [users, setUsers] = useState([]);
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [usersLoading, setUsersLoading] = useState(false);
  const [portalOpening, setPortalOpening] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);

  const isLiveDealer = dealer?.dataSource === "live_profile";
  const activeUsers = users.filter((user) => user.status === "active");
  const activeListings = dealer?.metrics?.activeListings ?? dealer?.activeListingCount ?? 0;

  const loadDealer = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const result = await fetchPartnerDealer(dealerId);
      setDealer(result.dealer);
      setForm({
        businessName: result.dealer.businessName || "",
        city: result.dealer.city || "",
        neighborhood: result.dealer.neighborhood || "",
        mainWhatsapp: result.dealer.mainWhatsapp || "",
        mainEmail: result.dealer.mainEmail || "",
        websiteUrl: result.dealer.websiteUrl || "",
        description: result.dealer.description || "",
        status: result.dealer.status || "active",
        publicProfileEnabled: Boolean(result.dealer.publicProfileEnabled),
        searchPriorityScore: result.dealer.searchPriorityScore ?? 0,
      });
    } catch (err) {
      setError(err.message || "Unable to load dealer.");
      setDealer(null);
      setForm(null);
    } finally {
      setLoading(false);
    }
  }, [dealerId]);

  const loadUsers = useCallback(async () => {
    if (!dealerId) return;
    try {
      setUsersLoading(true);
      const result = await fetchPartnerDealerUsers(dealerId);
      setUsers(result.users || []);
    } catch (err) {
      setError(err.message || "Unable to load dealer users.");
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  }, [dealerId]);

  useEffect(() => {
    loadDealer();
  }, [loadDealer]);

  useEffect(() => {
    if (dealer) {
      loadUsers();
    }
  }, [dealer, loadUsers]);

  useEffect(() => {
    if (!usersLoading && users.length === 0) {
      setShowCreateUser(true);
    }
  }, [users.length, usersLoading]);

  const handleChange = (field) => (event) => {
    const value =
      field === "publicProfileEnabled" ? event.target.checked : event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const result = await updatePartnerDealer(dealerId, {
        ...form,
        searchPriorityScore: Number(form.searchPriorityScore),
      });
      setDealer(result.dealer);
      setMessage("Dealer profile saved.");
    } catch (err) {
      setError(err.message || "Unable to save dealer.");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateUser = async (event) => {
    event.preventDefault();
    try {
      setError("");
      setMessage("");
      await createPartnerDealerUser(dealerId, userForm);
      setUserForm(emptyUserForm());
      setShowCreateUser(false);
      setMessage("Dealer portal user created.");
      await loadUsers();
    } catch (err) {
      setError(err.message || "Unable to create dealer user.");
    }
  };

  const handleDisableUser = async (userId) => {
    try {
      setError("");
      await updatePartnerDealerUser(dealerId, userId, { status: "disabled" });
      setMessage("Dealer portal user disabled.");
      await loadUsers();
    } catch (err) {
      setError(err.message || "Unable to update dealer user.");
    }
  };

  const handleOpenDealerPortal = async (dealerUserId) => {
    try {
      setPortalOpening(true);
      setError("");
      const result = await startPartnerDealerPortalSession(
        dealerId,
        dealerUserId ? { dealerUserId } : {}
      );
      rememberPartnerDealerPortalTarget(dealerId);
      window.location.assign(result.redirectUrl || "/v/automap/dealer/dashboard");
    } catch (err) {
      setError(err.message || "Unable to open dealer portal.");
      setPortalOpening(false);
    }
  };

  if (loading) {
    return (
      <div className="partner-dealer-account partner-dealer-account--loading panel-card">
        <p className="muted">Loading dealer workspace…</p>
      </div>
    );
  }

  if (!dealer || !form) {
    return (
      <div className="panel-card">
        <p className="admin-data-health-error">{error || "Dealer not found."}</p>
        <Link className="cv-button-link" to={path("dealers")}>
          Back to dealers
        </Link>
      </div>
    );
  }

  return (
    <div className="partner-dealer-account">
      <header className="partner-dealer-account__hero">
        <div className="partner-dealer-account__hero-main">
          <Link className="partner-dealer-account__back" to={path("dealers")}>
            ← All dealers
          </Link>
          <p className="partner-dealer-account__eyebrow">Dealer portal workspace</p>
          <h2>{dealer.businessName}</h2>
          <div className="partner-dealer-account__meta">
            {[dealer.city, dealer.neighborhood].filter(Boolean).join(" · ") || "Location not set"}
            <span className="partner-dealer-account__meta-sep">·</span>
            <span className={`partner-dealers-badge partner-dealers-badge--${dealer.status || "active"}`}>
              {formatStatusLabel(dealer.status)}
            </span>
            {isLiveDealer ? (
              <span className="partner-dealers-badge partner-dealers-badge--live">Live inventory</span>
            ) : (
              <span className="partner-dealers-badge partner-dealers-badge--managed">Portal managed</span>
            )}
          </div>
        </div>
        <div className="partner-dealer-account__hero-actions">
          <button
            type="button"
            className="partner-dashboard-btn partner-dashboard-btn--primary"
            disabled={portalOpening}
            onClick={() => handleOpenDealerPortal()}
          >
            {portalOpening ? "Opening portal…" : "View as dealer"}
          </button>
          <Link className="partner-dashboard-btn" to={path(`dealers/${dealerId}/inventory`)}>
            Manage inventory
          </Link>
        </div>
      </header>

      {error ? <div className="partner-dealer-account__alert partner-dealer-account__alert--error">{error}</div> : null}
      {message ? (
        <div className="partner-dealer-account__alert partner-dealer-account__alert--success">{message}</div>
      ) : null}

      <section className="partner-dealer-account__guide" aria-label="How to use this page">
        <h3 className="partner-dealer-account__guide-title">What you can do here</h3>
        <ol className="partner-dealer-account__guide-steps">
          <li className="partner-dealer-account__guide-step">
            <span className="partner-dealer-account__guide-num">1</span>
            <div>
              <strong>Preview the dealer experience</strong>
              <p>
                Open the dealer portal to see inventory, leads, and reports exactly as dealership staff
                would.
              </p>
              <button
                type="button"
                className="partner-dealer-account__guide-link"
                disabled={portalOpening}
                onClick={() => handleOpenDealerPortal()}
              >
                View as dealer →
              </button>
            </div>
          </li>
          <li className="partner-dealer-account__guide-step">
            <span className="partner-dealer-account__guide-num">2</span>
            <div>
              <strong>Set up portal logins</strong>
              <p>
                {activeUsers.length
                  ? `${activeUsers.length} active user${activeUsers.length === 1 ? "" : "s"} can sign in. Add or manage accounts below.`
                  : "No portal users yet — create one so the dealership can sign in on their own."}
              </p>
              <a className="partner-dealer-account__guide-link" href="#dealer-portal-users">
                Go to portal users →
              </a>
            </div>
          </li>
          <li className="partner-dealer-account__guide-step">
            <span className="partner-dealer-account__guide-num">3</span>
            <div>
              <strong>Update public profile &amp; search settings</strong>
              <p>Control how this dealer appears on AutoMapa and how their listings rank in search.</p>
              <a className="partner-dealer-account__guide-link" href="#account-settings">
                Edit profile settings →
              </a>
            </div>
          </li>
        </ol>
      </section>

      <div className="partner-dealer-account__layout">
        <main className="partner-dealer-account__main">
          <section className="partner-dealer-account__section" id="dealer-portal-users">
            <div className="partner-dealer-account__section-head">
              <div>
                <p className="partner-dealer-account__section-label">Portal access</p>
                <h3>Dealer portal users</h3>
                <p className="partner-dealer-account__section-desc">
                  Create logins for dealership staff. Use <strong>View as dealer</strong> to troubleshoot
                  or train them without sharing passwords.
                </p>
              </div>
              <button
                type="button"
                className="partner-dashboard-btn"
                disabled={portalOpening}
                onClick={() => handleOpenDealerPortal()}
              >
                View as dealer
              </button>
            </div>

            {isLiveDealer ? (
              <p className="partner-dealer-account__callout">
                Inventory for this dealer is ingested from live market sources. Manage listings from the
                Inventory tab; use portal users here for dealer sign-in and support.
              </p>
            ) : null}

            <div className="partner-dealer-account__card">
              {usersLoading ? <p className="muted">Loading dealer users…</p> : null}

              {!usersLoading ? (
                <>
                  {users.length ? (
                    <div className="partner-dealer-users__table-wrap">
                      <table className="partner-dealers-table partner-dealer-account__users-table">
                        <thead>
                          <tr>
                            <th>User</th>
                            <th>Role</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((user) => (
                            <tr key={user.id}>
                              <td>
                                <span className="partner-dealer-account__user-name">{user.name}</span>
                                <span className="partner-dealer-account__user-email">
                                  {user.email || "No email"}
                                </span>
                              </td>
                              <td>{ROLE_LABELS[user.role] || user.role}</td>
                              <td>
                                <UserStatusBadge status={user.status} />
                              </td>
                              <td>
                                <div className="partner-dealers-table__row-actions">
                                  {user.status === "active" ? (
                                    <button
                                      type="button"
                                      className="cv-button-link"
                                      disabled={portalOpening}
                                      onClick={() => handleOpenDealerPortal(user.id)}
                                    >
                                      Open portal
                                    </button>
                                  ) : null}
                                  {user.status !== "disabled" ? (
                                    <button
                                      type="button"
                                      className="cv-button-link"
                                      onClick={() => handleDisableUser(user.id)}
                                    >
                                      Disable
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="partner-dealer-account__empty">
                      <p>No portal users yet</p>
                      <p className="muted">
                        Add the first user below, or use <strong>View as dealer</strong> to preview the
                        portal immediately.
                      </p>
                    </div>
                  )}

                  <div className="partner-dealer-account__create-toggle">
                    <button
                      type="button"
                      className="partner-dashboard-btn"
                      onClick={() => setShowCreateUser((current) => !current)}
                    >
                      {showCreateUser ? "Hide create form" : "Create dealer user"}
                    </button>
                  </div>

                  {showCreateUser ? (
                    <form
                      className="partner-dealer-account__create-form"
                      onSubmit={handleCreateUser}
                    >
                      <h4>New portal user</h4>
                      <div className="partner-dealer-account__field-group">
                        <p className="partner-dealer-account__field-group-label">Person</p>
                        <div className="partner-dealer-form__grid">
                          <label>
                            Full name
                            <input
                              value={userForm.name}
                              onChange={(event) =>
                                setUserForm((current) => ({ ...current, name: event.target.value }))
                              }
                              placeholder="e.g. Ana García"
                              required
                            />
                          </label>
                          <label>
                            Email
                            <input
                              type="email"
                              value={userForm.email}
                              onChange={(event) =>
                                setUserForm((current) => ({ ...current, email: event.target.value }))
                              }
                              placeholder="for sign-in & notifications"
                            />
                          </label>
                          <label>
                            WhatsApp
                            <input
                              value={userForm.whatsapp}
                              onChange={(event) =>
                                setUserForm((current) => ({ ...current, whatsapp: event.target.value }))
                              }
                              placeholder="optional"
                            />
                          </label>
                        </div>
                      </div>
                      <div className="partner-dealer-account__field-group">
                        <p className="partner-dealer-account__field-group-label">Access</p>
                        <div className="partner-dealer-form__grid">
                          <label>
                            Role
                            <select
                              value={userForm.role}
                              onChange={(event) =>
                                setUserForm((current) => ({ ...current, role: event.target.value }))
                              }
                            >
                              {USER_ROLES.map((role) => (
                                <option key={role} value={role}>
                                  {ROLE_LABELS[role] || role}
                                </option>
                              ))}
                            </select>
                          </label>
                          <label>
                            Temporary password
                            <input
                              type="password"
                              minLength={8}
                              value={userForm.password}
                              onChange={(event) =>
                                setUserForm((current) => ({ ...current, password: event.target.value }))
                              }
                              placeholder="At least 8 characters"
                            />
                          </label>
                        </div>
                      </div>
                      <div className="partner-dealer-form__actions">
                        <button type="submit" className="partner-dashboard-btn partner-dashboard-btn--primary">
                          Create dealer user
                        </button>
                      </div>
                    </form>
                  ) : null}
                </>
              ) : null}
            </div>
          </section>

          <section className="partner-dealer-account__section" id="account-settings">
            <div className="partner-dealer-account__section-head">
              <div>
                <p className="partner-dealer-account__section-label">Public profile</p>
                <h3>Dealer profile &amp; search</h3>
                <p className="partner-dealer-account__section-desc">
                  Information shown on AutoMapa and settings that affect listing visibility and search
                  ranking.
                </p>
              </div>
            </div>

            <form className="partner-dealer-account__card partner-dealer-form" onSubmit={handleSave}>
              <div className="partner-dealer-account__field-group">
                <div className="partner-dealer-account__field-group-head">
                  <h4>Business identity</h4>
                  <p>Primary name and description buyers see on the dealer profile.</p>
                </div>
                <div className="partner-dealer-form__grid partner-dealer-form__grid--single">
                  <label>
                    Business name
                    <input
                      value={form.businessName}
                      onChange={handleChange("businessName")}
                      required
                    />
                  </label>
                </div>
                <label className="partner-dealer-form__full">
                  Description
                  <textarea
                    rows={4}
                    value={form.description}
                    onChange={handleChange("description")}
                    placeholder="Short summary of the dealership"
                  />
                </label>
              </div>

              <div className="partner-dealer-account__field-group">
                <div className="partner-dealer-account__field-group-head">
                  <h4>Location</h4>
                  <p>Helps buyers understand where this dealer operates.</p>
                </div>
                <div className="partner-dealer-form__grid">
                  <label>
                    City
                    <input value={form.city} onChange={handleChange("city")} />
                  </label>
                  <label>
                    Neighborhood
                    <input value={form.neighborhood} onChange={handleChange("neighborhood")} />
                  </label>
                </div>
              </div>

              <div className="partner-dealer-account__field-group">
                <div className="partner-dealer-account__field-group-head">
                  <h4>Contact channels</h4>
                  <p>How buyers and your team reach this dealership.</p>
                </div>
                <div className="partner-dealer-form__grid">
                  <label>
                    WhatsApp
                    <input value={form.mainWhatsapp} onChange={handleChange("mainWhatsapp")} />
                  </label>
                  <label>
                    Email
                    <input type="email" value={form.mainEmail} onChange={handleChange("mainEmail")} />
                  </label>
                  <label className="partner-dealer-form__full-span">
                    Website
                    <input
                      value={form.websiteUrl}
                      onChange={handleChange("websiteUrl")}
                      placeholder="https://"
                    />
                  </label>
                </div>
              </div>

              <div className="partner-dealer-account__field-group">
                <div className="partner-dealer-account__field-group-head">
                  <h4>Visibility &amp; search</h4>
                  <p>Control account status, public listing, and search priority.</p>
                </div>
                <div className="partner-dealer-form__grid">
                  <label>
                    Account status
                    <select value={form.status} onChange={handleChange("status")}>
                      {STATUS_OPTIONS.map((option) => (
                        <option key={option} value={option}>
                          {formatStatusLabel(option)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Search priority
                    <input
                      type="number"
                      min="0"
                      max="10000"
                      value={form.searchPriorityScore}
                      onChange={handleChange("searchPriorityScore")}
                    />
                    <span className="partner-dealer-account__field-hint">
                      Higher values rank this dealer&apos;s vehicles earlier in search.
                    </span>
                  </label>
                </div>
                <label className="partner-dealer-form__checkbox partner-dealer-account__toggle">
                  <input
                    type="checkbox"
                    checked={form.publicProfileEnabled}
                    onChange={handleChange("publicProfileEnabled")}
                  />
                  <span>
                    <strong>Public profile enabled</strong>
                    <span className="partner-dealer-account__field-hint">
                      Show this dealer on the public AutoMapa network.
                    </span>
                  </span>
                </label>
              </div>

              <div className="partner-dealer-account__form-footer">
                <button
                  type="submit"
                  className="partner-dashboard-btn partner-dashboard-btn--primary"
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save profile settings"}
                </button>
              </div>
            </form>
          </section>
        </main>

        <aside className="partner-dealer-account__aside">
          <div className="partner-dealer-account__stat-card">
            <p className="partner-dealer-account__stat-label">Active listings</p>
            <p className="partner-dealer-account__stat-value">{activeListings}</p>
            <Link className="partner-dealer-account__stat-link" to={path(`dealers/${dealerId}/inventory`)}>
              Open inventory →
            </Link>
          </div>
          <div className="partner-dealer-account__stat-card">
            <p className="partner-dealer-account__stat-label">Portal users</p>
            <p className="partner-dealer-account__stat-value">{activeUsers.length}</p>
            <a className="partner-dealer-account__stat-link" href="#dealer-portal-users">
              Manage users →
            </a>
          </div>
          <div className="partner-dealer-account__stat-card">
            <p className="partner-dealer-account__stat-label">Leads received</p>
            <p className="partner-dealer-account__stat-value">
              {dealer.metrics?.leadsReceived ?? dealer.leadsReceived ?? 0}
            </p>
          </div>
          <div className="partner-dealer-account__stat-card">
            <p className="partner-dealer-account__stat-label">Verification</p>
            <p className="partner-dealer-account__stat-value partner-dealer-account__stat-value--text">
              {formatStatusLabel(dealer.verificationStatus)}
            </p>
          </div>

          <div className="partner-dealer-account__aside-help">
            <h4>Quick tips</h4>
            <ul>
              <li>
                <strong>View as dealer</strong> opens the portal in support mode — use it before sharing
                login credentials.
              </li>
              <li>
                Live dealers: inventory edits happen on the <strong>Inventory</strong> tab; portal users
                are for dealer-facing tools only.
              </li>
              <li>
                Search priority is relative — small increases (10–50) are usually enough to nudge ranking.
              </li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
